import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ApiError } from '@awj/api-client';
import { formatMoney } from '@awj/i18n';
import { Button, Dialog, Skeleton, useToast } from '@awj/ui';

import { BilingualField } from '~/components/BilingualField.tsx';
import { EmptyState } from '~/components/EmptyState.tsx';
import { FieldHead } from '~/components/InfoHint.tsx';
import { PageTitle } from '~/components/PageTitle.tsx';
import { Select } from '~/components/Select.tsx';
import { OptionsEditor } from '~/components/catalog/OptionsEditor.tsx';
import { VariantMatrix } from '~/components/catalog/VariantMatrix.tsx';
import { ImageManager } from '~/components/catalog/ImageManager.tsx';
import { useI18n } from '~/i18n/index.tsx';
import {
  useArchiveProduct,
  useCategories,
  useDeleteProduct,
  useProduct,
  useSaveProduct,
} from '~/lib/catalog-queries.ts';
import {
  EMPTY_PRODUCT,
  slugify,
  toForm,
  toRequest,
  type ProductForm,
} from '~/lib/product-form.ts';
import { useSession } from '~/lib/session.tsx';
import { useUnsavedChanges } from '~/lib/unsaved-changes.tsx';

const STATUSES = ['Draft', 'Active', 'Archived'] as const;

/**
 * One product, in both languages.
 *
 * Everything is on one page rather than behind steps: an editor correcting a price should not
 * walk a wizard, and the fields that belong together — price beside compare-at, stock beside its
 * low threshold — are only comparable when they are visible together.
 */
export function ProductEditorPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const { has } = useSession();
  const { id: idParam } = useParams();

  const id = idParam && idParam !== 'new' ? Number(idParam) : null;
  const product = useProduct(id);
  const categories = useCategories();
  const save = useSaveProduct();
  const archive = useArchiveProduct();
  const remove = useDeleteProduct();

  const [form, setForm] = useState<ProductForm>(EMPTY_PRODUCT);
  // What the form held when it was loaded or last saved; anything else is unsaved.
  const [baseline, setBaseline] = useState<ProductForm>(EMPTY_PRODUCT);
  const unsaved = useUnsavedChanges(JSON.stringify(form) !== JSON.stringify(baseline));
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Keyed by the variant's local key, so a row keeps its message when the rows above it move.
  const [variantErrors, setVariantErrors] = useState<Record<string, string>>({});
  // Slugs follow the name until someone edits one, and then they stop — a published URL that
  // changes because a typo was fixed in the title breaks every link to it.
  const [slugsTouched, setSlugsTouched] = useState(false);
  const [focusInvalid, setFocusInvalid] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);

  /*
   * Load the product into the form once, not on every refetch.
   *
   * The panel refetches on window focus, and this editor is open for as long as it takes to write
   * a description. Re-applying the server's copy each time would throw away everything typed
   * since the page loaded, the moment someone switched to another tab and back.
   */
  const loadedId = useRef<number | null>(null);

  useEffect(() => {
    if (product.data && loadedId.current !== product.data.id) {
      loadedId.current = product.data.id;
      // One copy for both: toForm gives each variant row a fresh local key, so two calls never
      // compare equal and an untouched product would count as edited.
      const loaded = toForm(product.data);
      setForm(loaded);
      setBaseline(loaded);
      setSlugsTouched(true);
    }
  }, [product.data]);

  /*
   * Focus the first field that failed, *after* the render that marked it.
   *
   * Querying for `[aria-invalid]` straight after setting the errors finds nothing: the attribute
   * does not exist until React has re-rendered. The counter gives this an effect to hang on that
   * fires again on a second failed submit, which a boolean would not.
   */
  useEffect(() => {
    if (focusInvalid > 0) {
      document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
    }
  }, [focusInvalid]);

  function set<K extends keyof ProductForm>(key: K, value: ProductForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: '' }));
  }

  function setName(field: 'nameAr' | 'nameEn', value: string) {
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(slugsTouched
        ? {}
        : { [field === 'nameAr' ? 'slugAr' : 'slugEn']: slugify(value) }),
    }));

    // The slug's complaint goes with the name's while the slug still follows it — otherwise
    // typing a name fills the slug in and leaves "required" printed under a filled-in field.
    setErrors((current) => ({ ...current, [field]: '', ...(slugsTouched ? {} : { slugAr: '' }) }));
  }

  function validate(): boolean {
    const found: Record<string, string> = {};

    if (!form.nameAr.trim() || !form.nameEn.trim()) found.nameAr = t('catalog.bothLanguagesRequired');
    if (!form.slugAr.trim() || !form.slugEn.trim()) found.slugAr = t('catalog.bothLanguagesRequired');
    if (!form.sku.trim()) found.sku = t('catalog.required');
    if (form.price.trim() === '' || Number(form.price) <= 0) found.price = t('catalog.pricePositive');
    if (!form.categoryId) found.categoryId = t('catalog.required');

    setErrors(found);

    /*
     * Every variant needs a SKU of its own — the API rejects the whole product otherwise, and a
     * blanket "could not save" leaves nobody any wiser about which of six rows is at fault. A
     * newly built row starts empty, so this is the ordinary path, not a corner case.
     */
    const variantIssues: Record<string, string> = {};

    for (const variant of form.variants) {
      if (!variant.sku.trim()) {
        variantIssues[variant.key] = t('catalog.required');
      }
    }

    setVariantErrors(variantIssues);

    return Object.keys(found).length === 0 && Object.keys(variantIssues).length === 0;
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    if (!validate()) {
      setFocusInvalid((tick) => tick + 1);
      return;
    }

    try {
      const saved = await save.mutateAsync({ id, body: toRequest(form) });

      /*
       * Claim the record before the address changes.
       *
       * A new product moves to its own address, and the editor then reads it back and seeds the
       * form from what it finds. That seeding is right when you arrive at a product; it is wrong
       * here, where the form on screen is already the newer copy and may have been typed into
       * since the save. Marking the record as loaded now means the effect below has nothing to
       * do, and a correction made in the moment after saving is no longer thrown away.
       */
      loadedId.current = saved.id;
      setSlugsTouched(true);

      // The server makes a slug unique, and tidies its Arabic letters, before storing it. Show what it
      // kept rather than what was typed, and say so — that is the address the shop will use.
      const slugChanged = saved.slugAr !== form.slugAr || saved.slugEn !== form.slugEn;
      const stored = { ...form, slugAr: saved.slugAr, slugEn: saved.slugEn };

      setForm((current) => ({ ...current, slugAr: saved.slugAr, slugEn: saved.slugEn }));
      setBaseline(stored);
      unsaved.release();

      toast.show({
        title: t('catalog.saved'),
        message: slugChanged
          ? t('catalog.slugAdjusted', { slug: saved.slugEn !== form.slugEn ? saved.slugEn : saved.slugAr })
          : undefined,
        tone: 'success',
      });

      if (id === null) {
        navigate(`/products/${saved.id}`, { replace: true });
      }
    } catch (error) {
      toast.show({
        title: t('catalog.couldNotSave'),
        message: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'danger',
      });
    }
  }

  async function toggleArchived(productId: number, archived: boolean) {
    try {
      // Archive and restore answer 204, so there is no product to read back — and status is the
      // only field they move, so it is set here rather than by reloading the whole form over
      // whatever else is half-written.
      await archive.mutateAsync({ id: productId, archived });
      set('status', archived ? 'Active' : 'Archived');
      toast.show({ title: archived ? t('catalog.restored') : t('catalog.archived'), tone: 'success' });
    } catch (error) {
      // Awaited bare, a refusal was an unhandled rejection: no toast, and a button that simply
      // stopped spinning as though it had worked.
      toast.show({
        title: archived ? t('catalog.couldNotRestore') : t('catalog.couldNotArchive'),
        message: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'danger',
      });
    }
  }

  if (id !== null && product.isPending) {
    return (
      <>
        <PageTitle title={t('catalog.editProduct')} />
        <Skeleton height="420px" />
      </>
    );
  }

  if (id !== null && product.isError) {
    return (
      <>
        <PageTitle title={t('catalog.editProduct')} />
        <EmptyState
          icon="alert"
          title={t('catalog.productNotFound')}
          action={
            <Button variant="secondary" onClick={() => navigate('/products')}>
              {t('nav.products')}
            </Button>
          }
          tall
        />
      </>
    );
  }

  const archived = form.status === 'Archived';

  return (
    <form onSubmit={submit} noValidate>
      <PageTitle
        title={id === null ? t('catalog.newProduct') : t('catalog.editProduct')}
        back="/products"
        actions={
          <>
            <Button type="button" variant="ghost" onClick={() => navigate('/products')}>
              {t('common.cancel')}
            </Button>

            {id !== null ? (
              <Button
                type="button"
                variant="secondary"
                loading={archive.isPending}
                onClick={() => toggleArchived(id, archived)}
              >
                {archived ? t('catalog.restore') : t('catalog.archive')}
              </Button>
            ) : null}

            <Button type="submit" loading={save.isPending} loadingLabel={t('common.saving')}>
              {t('common.save')}
            </Button>
          </>
        }
      />

      <div className="editor">
        <div className="editor__main">
          <section className="card" aria-labelledby="basics-title">
            <h2 className="section-label" id="basics-title">
              {t('catalog.basics')}
            </h2>

            <BilingualField
              label={t('catalog.name')}
              required
              ar={form.nameAr}
              en={form.nameEn}
              onArChange={(value) => setName('nameAr', value)}
              onEnChange={(value) => setName('nameEn', value)}
              error={errors.nameAr}
            />

            <BilingualField
              label={t('catalog.slug')}
              required
              hint={t('catalog.slugHint')}
              ar={form.slugAr}
              en={form.slugEn}
              onArChange={(value) => {
                setSlugsTouched(true);
                set('slugAr', value);
              }}
              onEnChange={(value) => {
                setSlugsTouched(true);
                set('slugEn', value);
              }}
              error={errors.slugAr}
            />

            <BilingualField
              label={t('catalog.shortDescription')}
              hint={t('catalog.shortDescriptionHint')}
              multiline
              rows={2}
              ar={form.shortDescriptionAr}
              en={form.shortDescriptionEn}
              onArChange={(value) => set('shortDescriptionAr', value)}
              onEnChange={(value) => set('shortDescriptionEn', value)}
            />

            <BilingualField
              label={t('catalog.description')}
              multiline
              rows={6}
              ar={form.descriptionAr}
              en={form.descriptionEn}
              onArChange={(value) => set('descriptionAr', value)}
              onEnChange={(value) => set('descriptionEn', value)}
            />
          </section>

          <OptionsEditor form={form} onChange={setForm} />

          <VariantMatrix
            form={form}
            errors={variantErrors}
            onChange={(next) => {
              setForm(next);
              // Only the row just fixed loses its mark. Clearing the lot would tell someone who
              // typed one SKU that all six were dealt with.
              setVariantErrors((current) =>
                Object.fromEntries(
                  next.variants
                    .filter((variant) => current[variant.key] && !variant.sku.trim())
                    .map((variant) => [variant.key, current[variant.key]!]),
                ),
              );
            }}
          />

          {id !== null ? <ImageManager productId={id} images={product.data?.images ?? []} /> : null}

          {/*
            Last, and optional: the shop falls back to the name and the short description, so
            these only matter when a product should read differently in search results.
          */}
          <section className="card" aria-labelledby="seo-title">
            <h2 className="section-label" id="seo-title">
              {t('catalog.seo')}
            </h2>

            <BilingualField
              label={t('catalog.metaTitle')}
              hint={t('catalog.metaTitleHint')}
              ar={form.metaTitleAr}
              en={form.metaTitleEn}
              onArChange={(value) => set('metaTitleAr', value)}
              onEnChange={(value) => set('metaTitleEn', value)}
            />

            <BilingualField
              label={t('catalog.metaDescription')}
              hint={t('catalog.metaDescriptionHint')}
              multiline
              rows={2}
              ar={form.metaDescriptionAr}
              en={form.metaDescriptionEn}
              onArChange={(value) => set('metaDescriptionAr', value)}
              onEnChange={(value) => set('metaDescriptionEn', value)}
            />
          </section>
        </div>

        <aside className="editor__side">
          <section className="card" aria-labelledby="pricing-title">
            <h2 className="section-label" id="pricing-title">
              {t('catalog.pricing')}
            </h2>

            <label className="field">
              <span>
                {t('catalog.price')} <span className="required-mark" aria-hidden="true">*</span>
              </span>
              <input
                className="awj-input numeric"
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                aria-invalid={errors.price ? true : undefined}
                onChange={(event) => set('price', event.target.value)}
              />
              {errors.price ? <span className="field__error">{errors.price}</span> : null}
            </label>

            <div className="field">
              <FieldHead
                htmlFor="product-compare-at"
                label={t('catalog.compareAtPrice')}
                hint={t('catalog.compareAtHint')}
                hintId="product-compare-at-hint"
              />
              <input
                id="product-compare-at"
                className="awj-input numeric"
                type="number"
                min="0"
                step="0.01"
                value={form.compareAtPrice}
                aria-describedby="product-compare-at-hint"
                onChange={(event) => set('compareAtPrice', event.target.value)}
              />
            </div>

            {/* VAT is inclusive, so the shop's price is what a customer pays. Shown, not entered. */}
            {product.data ? (
              <p className="field__hint">
                {t('catalog.vatBreakdown', {
                  net: formatMoney(locale, product.data.netPrice),
                  vat: formatMoney(locale, product.data.vatAmount),
                })}
              </p>
            ) : null}
          </section>

          <section className="card" aria-labelledby="stock-title">
            <h2 className="section-label" id="stock-title">
              {t('catalog.stock')}
            </h2>

            {/*
              With variants, stock lives on each one and the product-level number is ignored. It
              used to stay editable here — showing 12 beside variants that summed to 13 — so it is
              replaced by that sum, read-only.
            */}
            {form.variants.length > 0 ? (
              <div className="field">
                <span>{t('catalog.stockQuantity')}</span>
                <output className="awj-input numeric" aria-live="polite">
                  {form.variants
                    .filter((variant) => variant.isActive)
                    .reduce((sum, variant) => sum + (Math.trunc(Number(variant.stockQuantity)) || 0), 0)}
                </output>
                <span className="field__hint">{t('catalog.stockIsPerVariant')}</span>
              </div>
            ) : (
              <label className="field">
                <span>{t('catalog.stockQuantity')}</span>
                <input
                  className="awj-input numeric"
                  type="number"
                  min="0"
                  step="1"
                  value={form.stockQuantity}
                  onChange={(event) => set('stockQuantity', event.target.value)}
                />
              </label>
            )}

            <div className="field">
              <FieldHead
                htmlFor="product-low-stock"
                label={t('catalog.lowStockThreshold')}
                hint={t('catalog.lowStockHint')}
                hintId="product-low-stock-hint"
              />
              <input
                id="product-low-stock"
                className="awj-input numeric"
                type="number"
                min="0"
                step="1"
                value={form.lowStockThreshold}
                aria-describedby="product-low-stock-hint"
                onChange={(event) => set('lowStockThreshold', event.target.value)}
              />
            </div>
          </section>

          <section className="card" aria-labelledby="placement-title">
            <h2 className="section-label" id="placement-title">
              {t('catalog.placement')}
            </h2>

            <div className="field">
              <label htmlFor="product-category">
                {t('catalog.category')} <span className="required-mark" aria-hidden="true">*</span>
              </label>
              <Select
                id="product-category"
                value={form.categoryId}
                invalid={!!errors.categoryId}
                onChange={(categoryId) => set('categoryId', categoryId)}
                placeholder={t('catalog.chooseCategory')}
                options={(categories.data ?? []).map((category) => ({
                  value: String(category.id),
                  label: locale === 'ar' ? category.nameAr : category.nameEn,
                }))}
              />
              {errors.categoryId ? <span className="field__error">{errors.categoryId}</span> : null}
            </div>

            <div className="field">
              <label htmlFor="product-status">{t('catalog.status')}</label>
              <Select
                id="product-status"
                value={form.status}
                onChange={(status) => set('status', status)}
                options={STATUSES.map((status) => ({
                  value: status,
                  label: t(`productStatus.${status}` as 'productStatus.Active'),
                }))}
              />
            </div>

            <label className="field">
              <span>{t('catalog.sku')} <span className="required-mark" aria-hidden="true">*</span></span>
              <input
                className="awj-input mono"
                value={form.sku}
                aria-invalid={errors.sku ? true : undefined}
                onChange={(event) => set('sku', event.target.value)}
              />
              {errors.sku ? <span className="field__error">{errors.sku}</span> : null}
            </label>

            <label className="checkbox">
              <input
                type="checkbox"
                checked={form.isFeatured}
                onChange={(event) => set('isFeatured', event.target.checked)}
              />
              <span>{t('catalog.featureOnHome')}</span>
            </label>
          </section>

          {/*
            Deleting a product is SuperAdmin-only at the API, so the card is not offered to
            anyone else — a button that always 403s is worse than no button.
          */}
          {id !== null && has('SuperAdmin') ? (
            <section className="card card--danger" aria-labelledby="danger-title">
              <h2 className="section-label" id="danger-title">
                {t('catalog.deleteProduct')}
              </h2>
              <p className="field__hint">{t('catalog.deleteHint')}</p>
              <Button
                type="button"
                /*
                 * Outline, not filled. The card already carries the warning; a solid red slab at
                 * the foot of the sidebar made deleting look like the page's main action.
                 */
                variant="secondary"
                className="awj-button--quietDanger"
                size="sm"
                onClick={() => setConfirmDelete(true)}
              >
                {t('common.delete')}
              </Button>
            </section>
          ) : null}
        </aside>
      </div>

      {id !== null ? (
        <Dialog
          open={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          title={t('catalog.deleteTitle', { name: form.nameEn || form.nameAr })}
          closeLabel={t('common.close')}
          size="sm"
          footer={
            <>
              <Button type="button" variant="ghost" onClick={() => setConfirmDelete(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                variant="danger"
                loading={remove.isPending}
                onClick={async () => {
                  try {
                    await remove.mutateAsync(id);
                    toast.show({ title: t('catalog.deleted'), tone: 'success' });
                    unsaved.release();
                    navigate('/products');
                  } catch (error) {
                    setConfirmDelete(false);
                    // The API refuses when an order references it. Saying so is more use than
                    // "could not delete".
                    toast.show({
                      title: t('catalog.couldNotDelete'),
                      message:
                        error instanceof ApiError && error.is('catalog.product_has_orders')
                          ? t('catalog.hasOrders')
                          : error instanceof ApiError
                            ? error.problem.detail
                            : undefined,
                      tone: 'danger',
                    });
                  }
                }}
              >
                {t('common.delete')}
              </Button>
            </>
          }
        >
          <p className="dialog-lede">{t('catalog.deleteExplain')}</p>
        </Dialog>
      ) : null}

      {unsaved.dialog}
    </form>
  );
}
